import React from 'react';
import { Phone, AlertTriangle, XCircle, Clock, FileText, Bell } from 'lucide-react';
export function TrafficViolationConvictionsPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-3480">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-3481">
        <div className="container mx-auto px-4" data-id="element-3482">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-3483">
            Traffic Violation Convictions
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-3484">
            While Driving a Commercial Motor Vehicle
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-3485">
        <div className="max-w-4xl mx-auto" data-id="element-3486">
          {/* Overview */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-3487">
            <div className="flex items-start" data-id="element-3488">
              <AlertTriangle className="text-red-600 mr-4 flex-shrink-0 mt-1" size={32} data-id="element-3489" />
              <div data-id="element-3490">
                <h2 className="text-2xl font-bold text-red-900 mb-4" data-id="element-3491">
                  Important Notice
                </h2>
                <p className="text-gray-800 mb-3" data-id="element-3492">
                  If you are convicted of certain traffic offenses while
                  operating a commercial motor vehicle (CMV), you will be
                  subject to <strong data-id="element-3493">more severe penalties</strong>, which may
                  result in the{' '}
                  <strong data-id="element-3494">suspension or revocation of your CDL</strong>.
                </p>
                <p className="text-gray-800 mb-3" data-id="element-3495">
                  Your operator/chauffeur license may also be affected,
                  depending on the severity of the traffic offense.
                </p>
                <p className="text-gray-800" data-id="element-3496">
                  <strong data-id="element-3497">
                    Any traffic conviction in a non-commercial motor vehicle
                    that results in a suspension or revocation of your
                    operator/chauffeur license, will also apply to your CDL.
                  </strong>
                </p>
              </div>
            </div>
          </div>

          {/* Major Traffic Offenses */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3498">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3499">
              <XCircle className="mr-3 text-[#B1050B]" size={28} data-id="element-3500" />
              Major Traffic Offenses
            </h2>

            <div className="bg-red-50 rounded-lg p-6 mb-6" data-id="element-3501">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-3502">
                Major Offenses Include:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-3503">
                <li className="flex items-start" data-id="element-3504">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3505">•</span>
                  <span data-id="element-3506">Under the influence of alcohol</span>
                </li>
                <li className="flex items-start" data-id="element-3507">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3508">•</span>
                  <span data-id="element-3509">
                    Having an alcohol concentration of{' '}
                    <strong data-id="element-3510">0.04 or greater</strong> while operating a CMV
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3511">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3512">•</span>
                  <span data-id="element-3513">
                    Operating under the influence of a controlled substance
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3514">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3515">•</span>
                  <span data-id="element-3516">Refusing to take an alcohol test</span>
                </li>
                <li className="flex items-start" data-id="element-3517">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3518">•</span>
                  <span data-id="element-3519">Leaving the scene of an accident</span>
                </li>
                <li className="flex items-start" data-id="element-3520">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3521">•</span>
                  <span data-id="element-3522">Using the vehicle to commit a felony</span>
                </li>
                <li className="flex items-start" data-id="element-3523">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3524">•</span>
                  <span data-id="element-3525">
                    Causing a fatality through the negligent operation of a CMV
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3526">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3527">•</span>
                  <span data-id="element-3528">
                    Using the vehicle in the commission of a felony involving
                    manufacturing, distributing or dispensing a controlled
                    substance
                  </span>
                </li>
              </ul>
            </div>

            <div className="space-y-4" data-id="element-3529">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3530">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3531">
                  If charged and convicted for any major traffic offense while
                  driving any type of motor vehicle, your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3532">
                  <li className="flex items-start" data-id="element-3533">
                    <span className="mr-2" data-id="element-3534">•</span>
                    <span data-id="element-3535">
                      <strong data-id="element-3536">Suspended for 1 year</strong> for a first
                      violation; <strong data-id="element-3537">3 years</strong> if transporting
                      hazardous materials
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3538">
                    <span className="mr-2" data-id="element-3539">•</span>
                    <span data-id="element-3540">
                      <strong data-id="element-3541">Revoked for a minimum of 10 years</strong> for any
                      two convictions occurring in separate incidents, in any
                      time period
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6" data-id="element-3542">
                <p className="text-gray-700 mb-2" data-id="element-3543">
                  If charged with and convicted of{' '}
                  <strong data-id="element-3544">
                    reckless driving, negligent homicide, or fleeing/eluding a
                    law enforcement officer (misdemeanor)
                  </strong>
                  , your CDL will be <strong data-id="element-3545">suspended for one year</strong>.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-3546">
                <p className="text-gray-700" data-id="element-3547">
                  If charged with and convicted of committing a{' '}
                  <strong data-id="element-3548">
                    felony involving the manufacture, distribution or dispensing
                    of a controlled substance
                  </strong>
                  , your CDL will be <strong data-id="element-3549">revoked for life</strong>.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-3550">
                <p className="text-sm text-gray-700" data-id="element-3551">
                  Your operator/chauffeur license may also be affected,
                  depending on the severity of the traffic conviction and points
                  will be added to your driving record.
                </p>
              </div>
            </div>
          </div>

          {/* Serious Traffic Violations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3552">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3553">
              <AlertTriangle className="mr-3 text-orange-600" size={28} data-id="element-3554" />
              Serious Traffic Violations
            </h2>

            <div className="bg-orange-50 rounded-lg p-6 mb-6" data-id="element-3555">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-3556">
                Serious Violations Include:
              </h3>
              <div className="grid md:grid-cols-2 gap-3" data-id="element-3557">
                <ul className="space-y-2 text-gray-700" data-id="element-3558">
                  <li className="flex items-start" data-id="element-3559">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3560">
                      •
                    </span>
                    <span data-id="element-3561">Speeding 15 mph or more over the posted limit</span>
                  </li>
                  <li className="flex items-start" data-id="element-3562">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3563">
                      •
                    </span>
                    <span data-id="element-3564">Following too closely</span>
                  </li>
                  <li className="flex items-start" data-id="element-3565">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3566">
                      •
                    </span>
                    <span data-id="element-3567">Changing/using lanes erratically/improperly</span>
                  </li>
                  <li className="flex items-start" data-id="element-3568">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3569">
                      •
                    </span>
                    <span data-id="element-3570">Operating a CMV without a CDL in possession</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-gray-700" data-id="element-3571">
                  <li className="flex items-start" data-id="element-3572">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3573">
                      •
                    </span>
                    <span data-id="element-3574">
                      A traffic violation connected with a fatal accident
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3575">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3576">
                      •
                    </span>
                    <span data-id="element-3577">Careless driving</span>
                  </li>
                  <li className="flex items-start" data-id="element-3578">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3579">
                      •
                    </span>
                    <span data-id="element-3580">Operating a CMV without a CDL</span>
                  </li>
                  <li className="flex items-start" data-id="element-3581">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3582">
                      •
                    </span>
                    <span data-id="element-3583">
                      Driving a CMV without proper class of CDL and/or
                      endorsements
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-4" data-id="element-3584">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3585">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3586">
                  If charged and convicted for any serious traffic violation
                  while driving a CMV, your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3587">
                  <li className="flex items-start" data-id="element-3588">
                    <span className="mr-2" data-id="element-3589">•</span>
                    <span data-id="element-3590">
                      <strong data-id="element-3591">Suspended for 60 days</strong> for two violations
                      in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3592">
                    <span className="mr-2" data-id="element-3593">•</span>
                    <span data-id="element-3594">
                      <strong data-id="element-3595">Suspended for 120 days</strong> for three or more
                      violations in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3596">
                    <span className="mr-2" data-id="element-3597">•</span>
                    <span data-id="element-3598">Points will be added to your driving record</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-4" data-id="element-3599">
                <p className="text-sm text-gray-700" data-id="element-3600">
                  If convicted for any of the above in a{' '}
                  <strong data-id="element-3601">non-CMV</strong>, your CDL will be suspended if the
                  conviction results in a suspension or revocation of your
                  driver's license.
                </p>
              </div>
            </div>
          </div>

          {/* Railroad Crossing Violations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3602">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3603">
              Railroad Crossing Violations
            </h2>

            <div className="space-y-6" data-id="element-3604">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-3605">
                <h3 className="font-bold text-blue-900 mb-3" data-id="element-3606">
                  For drivers who are NOT required to always stop:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3607">
                  <li className="flex items-start" data-id="element-3608">
                    <span className="text-blue-600 mr-2 flex-shrink-0" data-id="element-3609">•</span>
                    <span data-id="element-3610">
                      Failure to slow down and check the tracks are clear of an
                      approaching train
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3611">
                    <span className="text-blue-600 mr-2 flex-shrink-0" data-id="element-3612">•</span>
                    <span data-id="element-3613">
                      Failure to stop before reaching the crossing when the
                      tracks are not clear
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-3614">
                <h3 className="font-bold text-green-900 mb-3" data-id="element-3615">
                  For drivers who ARE always required to stop:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3616">
                  <li className="flex items-start" data-id="element-3617">
                    <span className="text-green-600 mr-2 flex-shrink-0" data-id="element-3618">•</span>
                    <span data-id="element-3619">
                      Failing to stop before driving onto the crossing
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-3620">
                <h3 className="font-bold text-purple-900 mb-3" data-id="element-3621">
                  For ALL drivers:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3622">
                  <li className="flex items-start" data-id="element-3623">
                    <span className="text-purple-600 mr-2 flex-shrink-0" data-id="element-3624">
                      •
                    </span>
                    <span data-id="element-3625">
                      Failing to have sufficient space to drive completely
                      through the crossing without stopping
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3626">
                    <span className="text-purple-600 mr-2 flex-shrink-0" data-id="element-3627">
                      •
                    </span>
                    <span data-id="element-3628">
                      Failing to obey a traffic control device or the directions
                      of an enforcement official at the crossing
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3629">
                    <span className="text-purple-600 mr-2 flex-shrink-0" data-id="element-3630">
                      •
                    </span>
                    <span data-id="element-3631">
                      Failing to negotiate a crossing because of insufficient
                      undercarriage clearance
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3632">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3633">
                  If charged and convicted for any railroad crossing violation,
                  your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3634">
                  <li className="flex items-start" data-id="element-3635">
                    <span className="mr-2" data-id="element-3636">•</span>
                    <span data-id="element-3637">
                      <strong data-id="element-3638">Suspended for 60 days</strong> for the first
                      violation
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3639">
                    <span className="mr-2" data-id="element-3640">•</span>
                    <span data-id="element-3641">
                      <strong data-id="element-3642">Suspended for 120 days</strong> for two violations
                      in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3643">
                    <span className="mr-2" data-id="element-3644">•</span>
                    <span data-id="element-3645">
                      <strong data-id="element-3646">Suspended for one year</strong> for three or more
                      violations in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3647">
                    <span className="mr-2" data-id="element-3648">•</span>
                    <span data-id="element-3649">Points will be added to your driving record</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Serious Alcohol Violations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3650">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3651">
              <Clock className="mr-3 text-[#B1050B]" size={28} data-id="element-3652" />
              Serious Alcohol Violations While Operating a CMV
            </h2>

            <div className="space-y-6" data-id="element-3653">
              <div className="bg-red-50 rounded-lg p-6" data-id="element-3654">
                <h3 className="font-bold text-red-900 mb-4" data-id="element-3655">
                  Out-of-Service (24 hours)
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-3656">
                  You will be placed out-of-service for 24 hours if you:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3657">
                  <li className="flex items-start" data-id="element-3658">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3659">•</span>
                    <span data-id="element-3660">Refuse to take a preliminary breath test (PBT)</span>
                  </li>
                  <li className="flex items-start" data-id="element-3661">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3662">•</span>
                    <span data-id="element-3663">
                      Consume alcohol within four hours prior to operating a CMV
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3664">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3665">•</span>
                    <span data-id="element-3666">Consume alcohol while operating a CMV</span>
                  </li>
                  <li className="flex items-start" data-id="element-3667">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3668">•</span>
                    <span data-id="element-3669">
                      Have a bodily alcohol content (BAC) of{' '}
                      <strong data-id="element-3670">.015 grams or more</strong> per 100 milliliters of
                      blood, per 210 liters of breath, or per 67 milliliters of
                      urine
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-lg p-6" data-id="element-3671">
                <h3 className="font-bold text-orange-900 mb-4" data-id="element-3672">
                  Other Alcohol Violations
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-3673">
                  If you have consumed too much alcohol, you can be charged
                  with:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3674">
                  <li className="flex items-start" data-id="element-3675">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3676">
                      •
                    </span>
                    <span data-id="element-3677">Operating While Intoxicated</span>
                  </li>
                  <li className="flex items-start" data-id="element-3678">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3679">
                      •
                    </span>
                    <span data-id="element-3680">Operating While Impaired by Liquor</span>
                  </li>
                  <li className="flex items-start" data-id="element-3681">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3682">
                      •
                    </span>
                    <span data-id="element-3683">
                      Operating with a BAC of{' '}
                      <strong data-id="element-3684">.04 &lt; .08 grams</strong> per 100 milliliters of
                      blood, per 210 liters of breath, or per 67 milliliters of
                      urine
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3685">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3686">
                      •
                    </span>
                    <span data-id="element-3687">
                      OUIL, OWI or UBAL causing death or serious injury
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3688">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3689">
                      •
                    </span>
                    <span data-id="element-3690">
                      Operating under the influence, or operating while impaired
                      by Controlled Substances
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3691">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3692">
                  If you refuse to submit to a chemical test (Implied Consent)*
                  or are convicted of any alcohol offense, your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3693">
                  <li className="flex items-start" data-id="element-3694">
                    <span className="mr-2" data-id="element-3695">•</span>
                    <span data-id="element-3696">
                      <strong data-id="element-3697">Suspended for one year</strong> for a first
                      offense, or <strong data-id="element-3698">three years</strong> if transporting
                      hazardous materials
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3699">
                    <span className="mr-2" data-id="element-3700">•</span>
                    <span data-id="element-3701">
                      <strong data-id="element-3702">Revoked for a minimum of ten years</strong> for a
                      second or third offense
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-4" data-id="element-3703">
                <p className="text-sm text-gray-700" data-id="element-3704">
                  <strong data-id="element-3705">Note:</strong> *Implied Consent means that any person
                  who holds a CDL is considered to have agreed to such testing.
                  Consent is implied by driving a motor vehicle.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-3706">
                <p className="text-sm text-gray-700" data-id="element-3707">
                  Your operator/chauffeur license may also be suspended or
                  revoked and points may be added to your driving record.
                </p>
              </div>
            </div>
          </div>

          {/* Notification Requirements */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-3708">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-3709">
              <Bell className="mr-3" size={32} data-id="element-3710" />
              CMV Operator's Responsibilities: Notification Requirements
            </h2>

            <div className="space-y-6" data-id="element-3711">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-3712">
                <h3 className="font-bold text-xl mb-4" data-id="element-3713">
                  You must notify your employer in writing of:
                </h3>

                <div className="space-y-4" data-id="element-3714">
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-3715">
                    <p className="font-semibold mb-2" data-id="element-3716">Traffic Violations</p>
                    <p className="text-gray-100 text-sm mb-3" data-id="element-3717">
                      (Other than a parking ticket) committed in any motor
                      vehicle <strong data-id="element-3718">within 30 days of conviction</strong>
                    </p>
                    <p className="text-gray-100 text-sm mb-2" data-id="element-3719">
                      The notification must include:
                    </p>
                    <ul className="space-y-1 text-sm text-gray-200 ml-4" data-id="element-3720">
                      <li data-id="element-3721">• Your full name and signature</li>
                      <li data-id="element-3722">• Driver license number</li>
                      <li data-id="element-3723">• Date of conviction</li>
                      <li data-id="element-3724">• Specific violation(s) committed</li>
                      <li data-id="element-3725">• Whether the violation was in a CMV</li>
                    </ul>
                  </div>

                  <div className="bg-white/10 rounded-lg p-4" data-id="element-3726">
                    <p className="font-semibold mb-2" data-id="element-3727">Out-of-Service Orders</p>
                    <p className="text-gray-100 text-sm" data-id="element-3728">
                      Any out-of-service order{' '}
                      <strong data-id="element-3729">within twenty-four hours</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Penalty Summary */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-3730">
            <h2 className="text-3xl font-bold mb-6" data-id="element-3731">Penalty Summary</h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-3732">
              <div data-id="element-3733">
                <h3 className="text-xl font-bold mb-3" data-id="element-3734">Major Offenses</h3>
                <ul className="space-y-2 text-sm" data-id="element-3735">
                  <li data-id="element-3736">• 1st violation: 1 year (3 years hazmat)</li>
                  <li data-id="element-3737">• 2 violations: 10 years minimum</li>
                  <li data-id="element-3738">• Drug felony: Life revocation</li>
                </ul>
              </div>

              <div data-id="element-3739">
                <h3 className="text-xl font-bold mb-3" data-id="element-3740">Serious Violations</h3>
                <ul className="space-y-2 text-sm" data-id="element-3741">
                  <li data-id="element-3742">• 2 violations in 3 years: 60 days</li>
                  <li data-id="element-3743">• 3+ violations in 3 years: 120 days</li>
                </ul>
              </div>

              <div data-id="element-3744">
                <h3 className="text-xl font-bold mb-3" data-id="element-3745">Railroad Crossings</h3>
                <ul className="space-y-2 text-sm" data-id="element-3746">
                  <li data-id="element-3747">• 1st violation: 60 days</li>
                  <li data-id="element-3748">• 2 violations in 3 years: 120 days</li>
                  <li data-id="element-3749">• 3+ violations in 3 years: 1 year</li>
                </ul>
              </div>

              <div data-id="element-3750">
                <h3 className="text-xl font-bold mb-3" data-id="element-3751">Alcohol Violations</h3>
                <ul className="space-y-2 text-sm" data-id="element-3752">
                  <li data-id="element-3753">• 1st offense: 1 year (3 years hazmat)</li>
                  <li data-id="element-3754">• 2nd/3rd offense: 10 years minimum</li>
                  <li data-id="element-3755">• Out-of-service: 24 hours</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-3756">
            <h2 className="text-3xl font-bold mb-4" data-id="element-3757">
              Need Help with CDL Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-3758">
              Expert guidance on traffic violations and CDL requirements
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-3759">
              <Phone className="mr-3" size={24} data-id="element-3760" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-3761">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}